import React, { useState, useRef, useEffect } from 'react';
import {
  Stack,
  TextField,
  Button,
  Alert,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  Typography,
  Box,
  Chip,
} from '@mui/material';
import { CoralFormData, StatusMessage, BatchItem } from '../types';
import { isValidSpeciesCode } from '../utils/validation';
import { playErrorBeep } from '../utils/audio';

interface SelectedParent {
  id: string;
}

interface CoralMetadataFormProps {
  selectedParent?: SelectedParent;
  getNextScan?: () => string | null;
  onFormDataChange?: (formData: CoralFormData) => void;
  selectedBatchItem?: BatchItem;
  onBatchItemUpdate?: (rfid: string, metadata: CoralFormData) => void;
  onEditComplete?: () => void;
  onScan?: (rfid: string) => boolean; // Returns true if added, false if duplicate
  hasItemsInBatch?: boolean;
  batchItems?: BatchItem[];
}

const CoralMetadataForm: React.FC<CoralMetadataFormProps> = ({
  selectedParent,
  getNextScan,
  onFormDataChange,
  selectedBatchItem,
  onBatchItemUpdate,
  onEditComplete,
  onScan,
  hasItemsInBatch = false,
  batchItems = [],
}) => {
  const [status, setStatus] = useState<StatusMessage | null>(null);
  const [globalFormData, setGlobalFormData] = useState<CoralFormData>({
    speciesCode: '',
    cutDate: '',
    condition: 'ALIVE',
  });
  // Disabled individual fragment editing state
  // const [editingFormData, setEditingFormData] = useState<CoralFormData | null>(null);
  const [rfidInput, setRfidInput] = useState<string>('');
  const [rfidChips, setRfidChips] = useState<string[]>([]);
  const rfidInputRef = useRef<HTMLInputElement>(null);

  // Auto-focus the RFID input field on component mount
  useEffect(() => {
    rfidInputRef.current?.focus();
  }, []);

  // Disabled individual fragment editing - always use global form data for batch creation
  const formData = globalFormData;

  // Disable metadata fields when we have items in batch (shared metadata for all)
  const metadataFieldsDisabled = hasItemsInBatch;

  const handleRfidChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Keep collecting characters until we have enough to process
    if (value.length < 24) {
      setRfidInput(value);
      return;
    }

    const potentialRfids: string[] = [];
    const existingRfids = new Set(rfidChips);
    let currentString = '';
    let foundDuplicate = false;

    for (let i = 0; i < value.length; i++) {
      const char = value[i];
      if (/[a-zA-Z0-9]/.test(char)) {
        currentString += char;

        if (currentString.length === 24) {
          // Check if this RFID already exists in chips
          if (existingRfids.has(currentString) || potentialRfids.includes(currentString)) {
            foundDuplicate = true;
          } else {
            // Check if RFID already exists in the batch (case-insensitive)
            const existsInBatch = batchItems.some(
              (item) => item.rfid.toLowerCase() === currentString.toLowerCase()
            );
            if (existsInBatch) {
              foundDuplicate = true;
            } else {
              potentialRfids.push(currentString);
              existingRfids.add(currentString);
            }
          }
          currentString = '';
        }
      } else {
        currentString = '';
      }
    }

    // Play error beep if any duplicates were found during scanning
    if (foundDuplicate) {
      playErrorBeep();
    }

    if (potentialRfids.length > 0) {
      setRfidChips((prev) => [...prev, ...potentialRfids]);
    }

    // Always clear processed input (incl. duplicate-only pastes) leaving only partial residue
    setRfidInput(currentString);
  };

  // Handle removing individual RFID chips
  const handleRemoveRfidChip = (rfidToRemove: string) => {
    setRfidChips((prev) => prev.filter((rfid) => rfid !== rfidToRemove));
  };

  const handleRfidSubmit = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && rfidChips.length > 0 && formData.speciesCode.trim() && onScan) {
      // Validate species code format before adding
      if (!isValidSpeciesCode(formData.speciesCode)) {
        playErrorBeep();
        setStatus({
          type: 'error',
          msg: 'Invalid Species Code format. Must be 1-32 characters using only uppercase letters, numbers, underscores, and hyphens.',
        });
        return;
      }

      // Add all RFID chips to the batch
      let addedCount = 0;
      let duplicateCount = 0;

      rfidChips.forEach((rfid) => {
        const wasAdded = onScan(rfid);
        if (wasAdded) {
          addedCount++;
        } else {
          duplicateCount++;
          playErrorBeep();
        }
      });

      // Clear processed chips and input
      setRfidChips([]);
      setRfidInput('');

      // Show status message
      if (addedCount > 0) {
        setStatus({
          type: 'success',
          msg: `Successfully added ${addedCount} fragment${addedCount > 1 ? 's' : ''} to batch.${
            duplicateCount > 0
              ? ` ${duplicateCount} duplicate${duplicateCount > 1 ? 's' : ''} were skipped.`
              : ''
          }`,
        });
        // Auto-dismiss success message after 3 seconds
        setTimeout(() => {
          setStatus(null);
        }, 3000);
      } else if (duplicateCount > 0) {
        setStatus({
          type: 'warning',
          msg: `All ${duplicateCount} RFID${duplicateCount > 1 ? 's' : ''} already exist in the batch.`,
        });
      }

      // Keep focus on the RFID input for continuous scanning
      setTimeout(() => {
        rfidInputRef.current?.focus();
      }, 0);
    }
  };

  const handleAddFragment = () => {
    if (rfidChips.length > 0 && formData.speciesCode.trim() && onScan) {
      // Validate species code format before adding
      if (!isValidSpeciesCode(formData.speciesCode)) {
        playErrorBeep();
        setStatus({
          type: 'error',
          msg: 'Invalid Species Code format. Must be 1-32 characters using only uppercase letters, numbers, underscores, and hyphens.',
        });
        return;
      }

      // Add all RFID chips to the batch
      let addedCount = 0;
      let duplicateCount = 0;

      rfidChips.forEach((rfid) => {
        const wasAdded = onScan(rfid);
        if (wasAdded) {
          addedCount++;
        } else {
          duplicateCount++;
          playErrorBeep();
        }
      });

      // Clear processed chips and input
      setRfidChips([]);
      setRfidInput('');

      // Show status message
      if (addedCount > 0) {
        setStatus({
          type: 'success',
          msg: `Successfully added ${addedCount} fragment${addedCount > 1 ? 's' : ''} to batch.${
            duplicateCount > 0
              ? ` ${duplicateCount} duplicate${duplicateCount > 1 ? 's' : ''} were skipped.`
              : ''
          }`,
        });
        // Auto-dismiss success message after 3 seconds
        setTimeout(() => {
          setStatus(null);
        }, 3000);
      } else if (duplicateCount > 0) {
        setStatus({
          type: 'warning',
          msg: `All ${duplicateCount} RFID${duplicateCount > 1 ? 's' : ''} already exist in the batch.`,
        });
      }

      // Focus back to the RFID input for continuous scanning
      setTimeout(() => {
        rfidInputRef.current?.focus();
      }, 0);
    }
  };

  const handleClearRfidChips = () => {
    setRfidChips([]);
    setRfidInput('');
    rfidInputRef.current?.focus();
  };

  // Handlers - simplified for batch creation (no individual editing)
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const updatedForm = { ...formData, [e.target.name]: e.target.value };
    // Always update global form data for batch creation
    setGlobalFormData(updatedForm);
    if (onFormDataChange) {
      onFormDataChange(updatedForm);
    }
  };

  const onSelectChange = (e: SelectChangeEvent) => {
    const updatedForm = { ...formData, [e.target.name]: e.target.value };
    // Always update global form data for batch creation
    setGlobalFormData(updatedForm);
    if (onFormDataChange) {
      onFormDataChange(updatedForm);
    }
  };

  return (
    <Stack spacing={1.5}>
      {/* RFID Input Section */}
      <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: 1, borderColor: 'grey.300' }}>
        <Typography variant="subtitle2" gutterBottom>
          Add New Fragments
        </Typography>
        <Stack spacing={1}>
          <Stack direction="row" spacing={1}>
            <TextField
              label="RFID Scanner Input"
              placeholder="Scan or type RFIDs here..."
              value={rfidInput}
              onChange={handleRfidChange}
              onKeyDown={handleRfidSubmit}
              autoFocus
              inputRef={rfidInputRef}
              sx={{ flex: 1 }}
              helperText={
                rfidChips.length > 0
                  ? `${rfidChips.length} RFID${rfidChips.length > 1 ? 's' : ''} ready - Press Enter to add to batch`
                  : 'RFIDs will appear as chips below as you scan them'
              }
            />
            <Button
              variant="contained"
              onClick={handleAddFragment}
              disabled={
                rfidChips.length === 0 ||
                !formData.speciesCode.trim() ||
                !isValidSpeciesCode(formData.speciesCode)
              }
              sx={{ height: '56px', alignSelf: 'flex-start' }}
            >
              Add All
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleClearRfidChips}
              disabled={rfidChips.length === 0 && !rfidInput}
              sx={{ height: '56px', alignSelf: 'flex-start' }}
            >
              Clear
            </Button>
          </Stack>

          {/* RFID Chips Display */}
          {rfidChips.length > 0 && (
            <Box
              sx={{
                p: 1,
                bgcolor: 'background.paper',
                borderRadius: 1,
                border: 1,
                borderColor: 'divider',
                minHeight: '40px',
                maxHeight: '100px', // Approximately 4 rows of chips (30px per row)
                overflowY: 'auto',
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.5,
                alignItems: 'flex-start',
              }}
            >
              {rfidChips.map((rfid, index) => (
                <Chip
                  key={`${rfid}-${index}`}
                  label={rfid}
                  onDelete={() => handleRemoveRfidChip(rfid)}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          )}
        </Stack>
      </Box>

      {metadataFieldsDisabled && (
        <Alert severity="info">
          Metadata is locked for batch creation. All fragments will use the same metadata.
        </Alert>
      )}

      {status && (
        <Alert severity={status.type} onClose={() => setStatus(null)}>
          {status.msg}
        </Alert>
      )}

      <TextField
        name="speciesCode"
        label="Species Code"
        value={formData.speciesCode}
        onChange={onChange}
        required
        disabled={metadataFieldsDisabled}
        error={formData.speciesCode.trim().length > 0 && !isValidSpeciesCode(formData.speciesCode)}
        helperText={
          formData.speciesCode.trim().length > 0 && !isValidSpeciesCode(formData.speciesCode)
            ? 'Species Code must be 1-32 characters using only uppercase letters, numbers, underscores, and hyphens'
            : 'Use uppercase letters, numbers, underscores, and hyphens only (e.g., ACRO_001)'
        }
      />
      <TextField
        name="cutDate"
        label="Cut Date"
        placeholder="YYYY-MM (e.g., 2025-11)"
        value={formData.cutDate}
        onChange={onChange}
        disabled={metadataFieldsDisabled}
        helperText="Optional: Format YYYY-MM. Defaults to current month if not provided."
      />

      <FormControl disabled={metadataFieldsDisabled}>
        <InputLabel id="condition-label">Condition</InputLabel>
        <Select
          labelId="condition-label"
          label="Condition"
          name="condition"
          value={formData.condition}
          onChange={onSelectChange}
        >
          <MenuItem value="ALIVE">ALIVE</MenuItem>
          <MenuItem value="DECEASED">DECEASED</MenuItem>
          <MenuItem value="EXPORTED">EXPORTED</MenuItem>
        </Select>
      </FormControl>

      <Button
        variant="outlined"
        onClick={() => {
          const clearedForm = { speciesCode: '', cutDate: '', condition: 'ALIVE' as const };
          setGlobalFormData(clearedForm);
          if (onFormDataChange) {
            onFormDataChange(clearedForm);
          }
        }}
        disabled={metadataFieldsDisabled}
      >
        Clear Metadata
      </Button>
    </Stack>
  );
};

export default CoralMetadataForm;
